import styled from 'styled-components';

import { amaranth } from '@paradigm/design-system/src/colors';
import { Resp, useApi } from '@paradigm/api/src/fetch-api';

import {
  openBillingPortal,
  startCreditPackCheckout,
  startSubscriptionCheckout,
} from '#/api/billing';
import toast from '#/components/toasts/toast';

import BalanceCard from './BalanceCard';
import CreditPackCard from './CreditPackCard';
import SubscriptionCard from './SubscriptionCard';
import { Hint } from './styles';
import useBillingMe from './useBillingMe';

// Defense in depth: the URLs we redirect the browser to come from a
// server response. If the backend (or anything between us and Stripe)
// were ever compromised or misconfigured, we'd otherwise blindly send
// users to whatever host that response named. Hard-allowlist the two
// Stripe origins we're meant to redirect to.
const STRIPE_REDIRECT_HOSTS = new Set([
  'checkout.stripe.com',
  'billing.stripe.com',
]);

const isSafeStripeUrl = (raw: string): boolean => {
  try {
    const u = new URL(raw);
    return u.protocol === 'https:' && STRIPE_REDIRECT_HOSTS.has(u.host);
  } catch {
    return false;
  }
};

/**
 * Block-Terminal billing settings panel.
 *
 * Stripe Checkout / the Billing Portal redirect the current tab —
 * the user goes to Stripe, finishes their flow, and Stripe returns
 * them to the desktop app via the configured success/cancel URLs.
 *
 * Calls the on-demand-markets Django API at `/billing/...` — the
 * same backend the desktop app already authenticates against.
 */
export default function SettingsBilling() {
  const { me, packs, initError } = useBillingMe();
  const [subState, execSubscribe] = useApi(startSubscriptionCheckout);
  const [portalState, execPortal] = useApi(openBillingPortal);
  const [packState, execBuyPack] = useApi(startCreditPackCheckout);

  // Action errors surface as toasts so the user keeps the panel context
  // (balance, status, pack list) instead of seeing it replaced by a red
  // string. Init-fetch failures still take over the panel — there's
  // nothing useful to show without `me` loaded.
  const onCheckoutResp =
    <T,>(pickUrl: (data: T) => string, fallbackError: string) =>
    (resp: Resp<T>) => {
      if (!resp.ok) {
        toast(resp.message || fallbackError);
        return;
      }
      const url = pickUrl(resp.data);
      if (!isSafeStripeUrl(url)) {
        toast('Got an unexpected redirect URL from the server.');
        return;
      }
      window.location.href = url;
    };

  const handleSubscribe = () =>
    execSubscribe(
      {},
      onCheckoutResp(
        (d) => d.checkout_url,
        'Could not start subscription checkout.',
      ),
    );

  const handleManage = () =>
    execPortal(
      {},
      onCheckoutResp((d) => d.portal_url, 'Could not open the billing portal.'),
    );

  const handleBuyPack = (priceId: string) =>
    execBuyPack(
      { priceId },
      onCheckoutResp(
        (d) => d.checkout_url,
        'Could not start credit-pack checkout.',
      ),
    );

  if (initError !== null) {
    return <ErrorHint>{initError}</ErrorHint>;
  }

  if (me === null) {
    return <Hint>Loading…</Hint>;
  }

  const pending =
    subState.isLoading || portalState.isLoading || packState.isLoading;

  return (
    <Stack>
      <BalanceCard credits={me.credits} />
      <SubscriptionCard
        subscription={me.subscription}
        pending={pending}
        onSubscribe={handleSubscribe}
        onManage={handleManage}
      />
      <CreditPackCard
        packs={packs}
        pending={pending}
        canBuyCredits={me.can_buy_credits}
        onBuy={handleBuyPack}
      />
    </Stack>
  );
}

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ErrorHint = styled.div`
  color: ${amaranth};
  font-size: 12px;
  line-height: 1.5;
`;
